function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { injectTestId } from "@cvent/nucleus-test-automation";
import React, { forwardRef, useMemo } from "react";
import { useStyle } from "./TextLink.style";
import { isDevEnv, devMessage } from "../../utils/devMessage";
import clsx from "clsx";
import useClassName from "../../utils/useClassName";
var isButtonTextLinkProps = function(props, element) {
    return element === 'button';
};
var isAnchorTextLinkProps = function(props, element) {
    return element === 'a';
};
var approvedElements = {
    a: 'a',
    button: 'button'
};
var STYLE_ONLY = 'style-only';
function Link(props) {
    var _bold = props.bold, bold = _bold === void 0 ? false : _bold, tmp = props.disabled, disabledProp = tmp === void 0 ? false : tmp, _element = props.element, element = _element === void 0 ? approvedElements.a : _element, forwardedRef = props.forwardedRef, icon = props.icon, _iconAlign = props.iconAlign, iconAlign = _iconAlign === void 0 ? 'start' : _iconAlign, _iconSize = props.iconSize, iconSize = _iconSize === void 0 ? 's' : _iconSize, _isBlock = props.isBlock, isBlock = _isBlock === void 0 ? false : _isBlock, _isRtl = props.isRtl, isRtl = _isRtl === void 0 ? false : _isRtl, linkTo = props.linkTo, neutral = props.neutral, size = props.size, testID = props.testID, text = props.text, variant = props.variant, classNameProp = props.className, rest = _objectWithoutProperties(props, [
        "bold",
        "disabled",
        "element",
        "forwardedRef",
        "icon",
        "iconAlign",
        "iconSize",
        "isBlock",
        "isRtl",
        "linkTo",
        "neutral",
        "size",
        "testID",
        "text",
        "variant",
        "className"
    ]);
    var iconPlacement = iconAlign;
    if (iconPlacement === 'start' && isRtl) {
        iconPlacement = 'end';
    } else if (isRtl) {
        iconPlacement = 'start';
    }
    if (isDevEnv()) {
        if (text) {
            devMessage('[TextLink] The text prop of the TextLink component is deprecated, please pass the text in as children');
        }
        if (linkTo) {
            devMessage('[TextLink] The linkTo prop of the TextLink component is deprecated, please use href');
        }
    }
    var disabled = disabledProp === true || undefined;
    var SIZES = {
        s: 'small',
        m: 'medium',
        l: 'large'
    };
    var isContrastDark = variant && (variant === 'accent' || variant === 'warning');
    var makeClass = useClassName({
        name: Link.displayName
    });
    var className = useMemo(function() {
        return clsx(makeClass(), (disabled || disabledProp === STYLE_ONLY) && makeClass(null, 'disabled'), bold && makeClass(null, 'bold'), isBlock && makeClass(null, 'block'), isRtl && makeClass(null, 'rtl'), size && makeClass(null, SIZES[size]), neutral && makeClass(null, 'neutral'), isContrastDark && makeClass(null, 'high-contrast-dark'), !!variant && !isContrastDark && makeClass(null, 'high-contrast-light'), classNameProp);
    }, [
        disabled,
        disabledProp,
        bold,
        isBlock,
        isRtl,
        size,
        neutral,
        isContrastDark,
        variant
    ]);
    var mainContainer = useStyle({
        disabled: disabled || disabledProp === STYLE_ONLY,
        iconAlign: iconPlacement,
        isBlock: isBlock,
        isRtl: iconPlacement === 'end' && isRtl,
        icon: icon,
        iconSize: iconSize,
        size: size,
        bold: bold,
        neutral: neutral,
        variant: variant
    }).mainContainer;
    var elementProps = _objectSpread({}, rest, injectTestId(testID), {
        className: className,
        css: mainContainer,
        disabled: disabled,
        tabIndex: disabled ? -1 : 0,
        'aria-disabled': disabledProp === STYLE_ONLY || undefined
    });
    if (isAnchorTextLinkProps(props, element)) {
        var href = props.href, onClick = props.onClick, onMouseDown = props.onMouseDown, children = props.children;
        elementProps = _objectSpread({}, elementProps, {
            href: disabled ? undefined : href || linkTo,
            onClick: disabled ? undefined : onClick,
            onMouseDown: function(e) {
                if (typeof onMouseDown === 'function') {
                    onMouseDown(e);
                }
                e.preventDefault();
            },
            ref: forwardedRef
        });
        var content = children || text;
        return _jsxs("a", _objectSpread({}, elementProps, {
            children: [
                icon && iconPlacement === 'start' && icon,
                icon && isBlock ? _jsx("span", {
                    children: content
                }) : content,
                icon && iconPlacement === 'end' && icon
            ]
        }));
    }
    if (isButtonTextLinkProps(props, element)) {
        var onClick1 = props.onClick, onMouseDown1 = props.onMouseDown, children1 = props.children;
        elementProps = _objectSpread({}, elementProps, {
            onClick: disabled ? undefined : onClick1,
            onMouseDown: function(e) {
                if (typeof onMouseDown1 === 'function') {
                    onMouseDown1(e);
                }
                e.preventDefault();
            },
            ref: forwardedRef
        });
        var _type;
        return _jsxs("button", _objectSpread({}, elementProps, {
            type: (_type = elementProps.type) !== null && _type !== void 0 ? _type : 'button',
            children: [
                icon && iconPlacement === 'start' && icon,
                children1 || text,
                icon && iconPlacement === 'end' && icon
            ]
        }));
    }
    var onClick2 = props.onClick, onMouseDown2 = props.onMouseDown, href1 = props.href;
    var children2 = props.children;
    elementProps = _objectSpread({}, elementProps, {
        href: disabled ? undefined : href1 || linkTo,
        onClick: disabled ? undefined : onClick2,
        onMouseDown: function(e) {
            if (typeof onMouseDown2 === 'function') {
                onMouseDown2(e);
            }
            e.preventDefault();
        },
        ref: forwardedRef
    });
    var Elem = element;
    return _jsxs(Elem, _objectSpread({}, elementProps, {
        children: [
            icon && iconPlacement === 'start' && icon,
            children2 || text,
            icon && iconPlacement === 'end' && icon
        ]
    }));
}
var beforeForward = function(props, ref) {
    var prefetch = props.prefetch, rest = _objectWithoutProperties(props, [
        "prefetch"
    ]);
    if (isAnchorTextLinkProps(props, props.element || approvedElements.a)) {
        if (props.href && typeof prefetch === 'function') {
            prefetch(props.href);
        }
    }
    return _jsx(Link, _objectSpread({}, _objectSpread({}, rest, {
        forwardedRef: ref
    })));
};
export var TextLink = forwardRef(beforeForward);
Link.displayName = 'TextLink';
